import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataManager } from '@syncfusion/ej2-data';
import { Observable, forkJoin, merge, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { DocumentReadModel } from 'src/app/models/documents-service/document-read-model';
import { EntrepriseSecondaire } from 'src/app/models/entreprise/entreprise.model';
import { ValeurListeFixeModel } from 'src/app/models/listes-fixes/valeur-liste-fixe.model';
import { ExerciceDominantModel } from 'src/app/models/personnes/exercice-dominant.model';
import { ODataAdaptor } from '../../adaptors/odata-adaptor';
import { FileDownloaderService } from '../../common/file-downloader.service';
import { UploadUtilService } from '../../common/photo-uploader/upload-util.service';
import { ValeurListeOfficielleModel } from '../../models/listes-fixes/valeur-liste-officielle.model';
import { PersonneModel } from '../../models/personnes/personne.model';
import { SessionColonneChampAdditionnel } from '../../models/sessions/session-colonne-champ-additionnel.model';
import { ResponseCardModel } from '../../models/toasts/response-card-model';
import { ToastSuccesModel } from '../../models/toasts/toast-succes.model';
import { IChampPersoViewModel } from '../../pages/champs-perso/models/champ-perso-models';
import { FileConversionsUtil, base64string } from '../../utils/file-conversions-util';
import { ConfigService } from '../config/config.service';
import { NotificationToastService } from '../notifications/notification-toast.service';
import { LocalStorageTokenService } from '../utilisateurs/local-storage-token.service';

@Injectable({
  providedIn: 'root',
})
export class PersonnesService {
  private readonly baseUrlApi = this.configService.config.urlApi;

  constructor(
    private oDataAdaptor: ODataAdaptor,
    private client: HttpClient,
    private fileDownloaderService: FileDownloaderService,
    private notificationToastService: NotificationToastService,
    private uploadUtilService: UploadUtilService,
    private localStorageTokenService: LocalStorageTokenService,
    private configService: ConfigService
  ) {}

  buildDataManagerGetPersonnes(): DataManager {
    return new DataManager({
      url: `${this.baseUrlApi}/api/crm/PersonnesViewNew`,
      adaptor: this.oDataAdaptor,
    });
  }


  public getFormateurForLink(idPersonne: number, recherche?: string):Observable<any>{
    return this.client.get(`${this.baseUrlApi}/api/crm/FormateursView?$filter=Statut eq 'Actif'&$orderby=Nom,Prenom&idPersonne=${idPersonne}&recherche=${recherche ? recherche : ''}`);
  }

  getChampsAdditionnels(): Observable<IChampPersoViewModel[]>{
    return this.client.get<IChampPersoViewModel[]>(`${this.baseUrlApi}/api/crm/ChampsPersoPersonne/GetChamps`)
  }

  public getColonnesChampsAdditionnelsPersonne(): Observable<SessionColonneChampAdditionnel[]> {
    return this.client.get<Record<string, unknown>[]>(
      `${this.baseUrlApi}/api/crm/Personnes/GetColonnesChampsAdditionnelsPersonne`,
    ).pipe(
      map(colonnes => (colonnes ?? []).map(raw => ({
        guid: String(raw['guid'] ?? raw['Guid'] ?? ''),
        nom: String(raw['nom'] ?? raw['Nom'] ?? ''),
        type: Number(raw['type'] ?? raw['Type'] ?? 0),
        values: (raw['values'] ?? raw['Values'] ?? null) as string[] | null,
        cle: (raw['cle'] ?? raw['Cle'] ?? null) as string | null,
      }))),
    );
  }

  public getExportComplet(filter: Object): Observable<any> {
    return this.client.post(`${this.baseUrlApi}/api/crm/ExportCompletPersonne`, filter, { responseType: 'blob' as 'json' });
  }

  buildDataManagerGetPersonnesForEntreprise(entrepriseId : number): DataManager {
    return new DataManager({
      url: `${this.baseUrlApi}/api/crm/PersonnesEntreprisesView?IdEntreprise=${entrepriseId}`,
      adaptor: this.oDataAdaptor,
    });
  }

  addPersonne(personne: PersonneModel): Observable<ResponseCardModel> {
    return this.client
      .put<ResponseCardModel>(`${this.baseUrlApi}/api/crm/personnes/addpersonne`, personne)
      .pipe(tap(response => this.notificationToastService.pushFromCard(response)));
  }

  public addPersonneFromReferentiel(personne: PersonneModel): Observable<ResponseCardModel> {
    return this.client
      .get<ResponseCardModel>(`${this.baseUrlApi}/api/crm/referentiel/${personne.id}`)
      .pipe(tap(response => this.notificationToastService.pushFromCard(response)));
  }

  updatePersonne(personne: PersonneModel): Observable<number> {
    return this.client.post(`${this.baseUrlApi}/api/crm/personnes/updatePersonne`, personne).pipe(
      tap((idPersonne: number) => {
        this.notificationToastService.push(new ToastSuccesModel('La personne a bien été modifiée.'));
        return idPersonne;
      }),
    );
  }

  getPersonneDetaillee(idPersonne: number): Observable<PersonneModel> {
    return this.client.get<PersonneModel>(`${this.baseUrlApi}/api/crm/personnes/getpersonne/${idPersonne}`).pipe(
      map(p => {
        if (p.dateDeNaissance != null) {
          p.dateDeNaissance = new Date(p.dateDeNaissance);
        }
        return p;
      }),
    );
  }

  chargerPhotoAsBase64(idPersonne: number): Observable<base64string> {
    return this.fileDownloaderService
      .fetchFile(`${this.baseUrlApi}/api/crm/IndividusPhoto/${idPersonne}`, null)
      .pipe(
        switchMap((blob: Blob) => {
          if (blob.size == 0) {
            return of(null);
          } else {
            return FileConversionsUtil.imageBlobToBase64(blob);
          }
        }),
        catchError(err => {
          return of(null);
        })
      );
  }


  televerserPhoto(photo: File, idIndividu: number) {
    return this.uploadUtilService.uploadFile(`${this.baseUrlApi}/api/crm/IndividusPhoto`, {File: photo, idIndividu})
      .pipe(
        tap(() => this.notificationToastService.push(new ToastSuccesModel(`Fichier téléversé avec succès`)))
      );
  }

  activerOuDesactiverPersonne(personne: PersonneModel) {
      const statusText = personne.actif === true ? 'activée' : 'désactivée';
      return this.client
        .post(`${this.baseUrlApi}/api/crm/personnes/ToggleActivationPersonne`, {
          id: personne.id,
          actif: personne.actif,
        })
        .pipe(
          tap(() => this.notificationToastService.push(new ToastSuccesModel(`La personne a été ${statusText}`))),
        );
  }

  supprimerPhoto(idPersonne: number): Observable<void> {
    return this.client.delete<void>(`${this.baseUrlApi}/api/crm/IndividusPhoto/${idPersonne}`)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Photo supprimée avec succès`))))
  }

  loadPhotoPersonnesAsyncTest(personnes: { id: number }[]): Observable<{ personne: any; photoUrl: string | null }[]> {
    const observables = personnes.map(personne =>
      this.chargerPhotoAsBase64(personne.id).pipe(
        map(photoUrl => ({ personne, photoUrl })) // photoUrl peut être null
      )
    );
    return forkJoin(observables);
  }

  loadPhotoPersonnesAsync(personnes: { id: number}[]): Observable<{personne: any, photoUrl: any}> {
    return merge(...personnes.map(personne => this.chargerPhotoAsBase64(personne.id)
      .pipe(map(photoUrl => {
        return {personne, photoUrl}
      }))))
  }

  buildDataManagerGetDocumentsPersonne(idPersonne) {
    return new DataManager({
      url: `${this.baseUrlApi}/api/crm/personnes/GetDocuments/${idPersonne}`,
      adaptor: this.oDataAdaptor,
    });
  }

  buildDataManagerGetEmailsPersonne(idPersonne) {
    return new DataManager({
      url: `${this.baseUrlApi}/api/crm/personnes/GetEmails/${idPersonne}`,
      adaptor: this.oDataAdaptor,
    });
  }

  getDocumentDownloadUrl(id: number): string {
    return `${this.baseUrlApi}/api/crm/personnes/GetDocument/${id}`;
  }

  televerserDocument(document: File, idPersonne: number, updateFunction: () => any = null): Observable<DocumentReadModel> {
    return this.uploadUtilService
      .uploadFile(`${this.baseUrlApi}/api/crm/personnes/addDocument`, {File: document, idPersonne: idPersonne}, updateFunction)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Fichier téléversé avec succès`))));
  }

  supprimerDocument(idDocument: string):Observable<any> {
    return this.client.delete(`${this.baseUrlApi}/api/crm/personnes/SupprimerDocument/${idDocument}`)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Document supprimé avec succès`))));
  }

  getListeDernierDiplomeOuTitre(): Observable<Array<ValeurListeOfficielleModel>> {
    return this.client.get<Array<ValeurListeOfficielleModel>>(`${this.baseUrlApi}/api/crm/personnes/getListeDernierDiplomeOuTitre`);
  }

  getListeDerniereClasseAnnee(): Observable<Array<ValeurListeOfficielleModel>> {
    return this.client.get<Array<ValeurListeOfficielleModel>>(`${this.baseUrlApi}/api/crm/personnes/getListeDerniereClasseAnnee`);
  }

  getListeRegimeSocial(): Observable<Array<ValeurListeOfficielleModel>> {
    return this.client.get<Array<ValeurListeOfficielleModel>>(`${this.baseUrlApi}/api/crm/personnes/getListeRegimeSocial`);
  }

  getListeExerciceDominant(): Observable<Array<ExerciceDominantModel>> {
    return this.client.get<Array<ExerciceDominantModel>>(`${this.baseUrlApi}/api/crm/personnes/getListeExerciceDominant`);
  }

  public getStates(): Observable<ValeurListeFixeModel[]>{
    return this.client.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/crm/ListeFixeEtatsPersonne/getValeurs`);
  }

  public checkIsAlreadyExist(personne: PersonneModel): Observable<any>{
    return this.client.post(`${this.baseUrlApi}/api/crm/personnes/DuplicatePersonCheck`, personne);
  }

  public getEntrepriseSecondaire(idEntreprisePrincipale: number, idPersonne?: number, recherche?: string): Observable<EntrepriseSecondaire[]>{
    if(idPersonne){
      return this.client.get<EntrepriseSecondaire[]>(`${this.baseUrlApi}/api/crm/personnes/GetEntreprisesSecondaires?idEntreprisePrincipale=${idEntreprisePrincipale}&idIndividu=${idPersonne}&recherche=${recherche ? recherche: ''}`)
    }else{
      return this.client.get<EntrepriseSecondaire[]>(`${this.baseUrlApi}/api/crm/personnes/GetEntreprisesSecondaires?idEntreprisePrincipale=${idEntreprisePrincipale}&recherche=${recherche ? recherche: ''}`)
    }
  }
}
